import { useState } from 'react'
import { AlertTriangle, CalendarCheck, CalendarPlus, Check, UserPlus, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { Call } from '../../api/telemetry'
import {
  useApproveCallback,
  useApproveContactUpdate,
} from '../../api/telemetry'

export function PendingProposalsPanel({ call }: { call: Call }) {
  const contactPending = call.contact_update_status === 'pending'
  const callbackPending = call.callback_status === 'pending'
  const contactApproved = call.contact_update_status === 'approved'
  const callbackApproved = call.callback_status === 'approved'

  // Hide entirely when there's nothing actionable AND nothing to summarize.
  if (!contactPending && !callbackPending && !contactApproved && !callbackApproved) {
    return null
  }

  return (
    <div className="space-y-3">
      {(contactPending || contactApproved) && <ContactUpdateCard call={call} />}
      {(callbackPending || callbackApproved) && <CallbackCard call={call} />}
    </div>
  )
}

function ContactUpdateCard({ call }: { call: Call }) {
  const { t } = useTranslation('calls')
  const mutation = useApproveContactUpdate(call.call_sid)
  const [firstName, setFirstName] = useState(call.extracted_first_name ?? '')
  const [lastName, setLastName] = useState(call.extracted_last_name ?? '')
  const [email, setEmail] = useState(call.extracted_email ?? '')
  const [birthDate, setBirthDate] = useState(call.extracted_birth_date ?? '')

  if (call.contact_update_status === 'approved') {
    const display = [call.extracted_first_name, call.extracted_last_name]
      .filter(Boolean)
      .join(' ')
    return (
      <div className="flex items-center gap-2 rounded-lg border border-success/30 bg-success/5 px-3 py-2 text-sm text-text-secondary">
        <Check className="h-4 w-4 text-success" />
        <span>
          {t('detail.proposals.contactUpdate.savedSummary', {
            name: display || t('detail.proposals.contactUpdate.noName'),
          })}
        </span>
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-border bg-surface-card p-4">
      <div className="flex items-center gap-2">
        <UserPlus className="h-4 w-4 text-primary" />
        <h4 className="text-sm font-semibold text-text-primary">
          {t('detail.proposals.contactUpdate.title')}
        </h4>
      </div>
      <p className="mt-1 text-xs text-text-secondary">
        {t('detail.proposals.contactUpdate.description')}
      </p>
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <label className="text-xs font-medium text-text-secondary">
          {t('detail.proposals.contactUpdate.firstName')}
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="mt-1 w-full rounded-md border border-border bg-surface px-2 py-1 text-sm text-text-primary"
          />
        </label>
        <label className="text-xs font-medium text-text-secondary">
          {t('detail.proposals.contactUpdate.lastName')}
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className="mt-1 w-full rounded-md border border-border bg-surface px-2 py-1 text-sm text-text-primary"
          />
        </label>
        <label className="text-xs font-medium text-text-secondary">
          {t('detail.proposals.contactUpdate.email')}
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded-md border border-border bg-surface px-2 py-1 text-sm text-text-primary"
          />
        </label>
        <label className="text-xs font-medium text-text-secondary">
          {t('detail.proposals.contactUpdate.birthDate')}
          <input
            type="date"
            value={birthDate}
            onChange={(e) => setBirthDate(e.target.value)}
            className="mt-1 w-full rounded-md border border-border bg-surface px-2 py-1 text-sm text-text-primary"
          />
        </label>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          disabled={mutation.isPending}
          onClick={() =>
            mutation.mutate({
              action: 'approve',
              first_name: firstName.trim() || null,
              last_name: lastName.trim() || null,
              email: email.trim() || null,
              birth_date: birthDate.trim() || null,
            })
          }
          className="inline-flex items-center gap-1.5 rounded bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-primary/90 disabled:opacity-60"
        >
          <Check className="h-3.5 w-3.5" />
          {t('detail.proposals.apply')}
        </button>
        <button
          type="button"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate({ action: 'dismiss' })}
          className="inline-flex items-center gap-1.5 rounded border border-border bg-surface px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-elevated"
        >
          <X className="h-3.5 w-3.5" />
          {t('detail.proposals.dismiss')}
        </button>
      </div>
    </div>
  )
}

function CallbackCard({ call }: { call: Call }) {
  const { t } = useTranslation('calls')
  const mutation = useApproveCallback(call.call_sid)
  const initialIso = call.callback_at ?? ''
  const [whenLocal, setWhenLocal] = useState(toLocalInput(initialIso))
  const [reason, setReason] = useState(call.callback_reason ?? '')

  if (call.callback_status === 'approved') {
    const when = call.callback_at ? formatWhen(call.callback_at) : ''
    return (
      <div className="flex items-center gap-2 rounded-lg border border-success/30 bg-success/5 px-3 py-2 text-sm text-text-secondary">
        <CalendarCheck className="h-4 w-4 text-success" />
        <span>{t('detail.proposals.callback.savedSummary', { when })}</span>
      </div>
    )
  }

  return (
    <div className="rounded-xl border border-border bg-surface-card p-4">
      <div className="flex items-center gap-2">
        <CalendarPlus className="h-4 w-4 text-primary" />
        <h4 className="text-sm font-semibold text-text-primary">
          {t('detail.proposals.callback.title')}
        </h4>
      </div>
      {call.callback_needs_review && (
        <div className="mt-2 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning">
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
          <span>{t('detail.proposals.callback.needsReview')}</span>
        </div>
      )}
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr]">
        <label className="text-xs font-medium text-text-secondary">
          {t('detail.proposals.callback.when')}
          <input
            type="datetime-local"
            value={whenLocal}
            onChange={(e) => setWhenLocal(e.target.value)}
            className="mt-1 w-full rounded-md border border-border bg-surface px-2 py-1 text-sm text-text-primary"
          />
        </label>
        <label className="text-xs font-medium text-text-secondary">
          {t('detail.proposals.callback.reason')}
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t('detail.proposals.callback.reasonPlaceholder')}
            className="mt-1 w-full rounded-md border border-border bg-surface px-2 py-1 text-sm text-text-primary"
          />
        </label>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          disabled={mutation.isPending || !whenLocal}
          onClick={() =>
            mutation.mutate({
              action: 'approve',
              callback_at: localInputToIso(whenLocal),
              callback_reason: reason || null,
            })
          }
          className="inline-flex items-center gap-1.5 rounded bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-primary/90 disabled:opacity-60"
        >
          <Check className="h-3.5 w-3.5" />
          {t('detail.proposals.callback.approveAndSchedule')}
        </button>
        <button
          type="button"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate({ action: 'dismiss' })}
          className="inline-flex items-center gap-1.5 rounded border border-border bg-surface px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-elevated"
        >
          <X className="h-3.5 w-3.5" />
          {t('detail.proposals.dismiss')}
        </button>
      </div>
      {mutation.isSuccess && mutation.data.calendar_status === 'skipped_no_integration' && (
        <p className="mt-2 text-xs text-text-secondary">
          {t('detail.proposals.callback.connectCalendarHint')}
        </p>
      )}
      {mutation.isSuccess && mutation.data.calendar_html_link && (
        <a
          href={mutation.data.calendar_html_link}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-block text-xs text-primary hover:underline"
        >
          {t('detail.proposals.callback.openCalendarEvent')}
        </a>
      )}
    </div>
  )
}

// ── Helpers ──────────────────────────────────────────────────────────

function toLocalInput(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  // datetime-local needs "YYYY-MM-DDTHH:MM" in local time.
  const pad = (n: number) => n.toString().padStart(2, '0')
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  )
}

function localInputToIso(local: string): string {
  if (!local) return ''
  // datetime-local has no timezone — interpret as local and convert to ISO.
  const d = new Date(local)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString()
}

function formatWhen(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}
